import { useTranslation } from 'react-i18next';
import { ExternalLink, RotateCw } from 'lucide-react';

import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TableCell, TableRow } from '@/components/ui/table';
import { formatDate } from '@/lib/format';

import { CopyableValue } from './CopyableValue';
import { formatBps, safeHttpUrl, truncateMiddle } from './format';
import type { PlatformPayout } from './types';

type Props = {
  payout: PlatformPayout;
  /** The viewer may queue a manual sweep (superadmin). */
  canRetry: boolean;
  isRetrying: boolean;
  onRetry: (payoutId: string) => void;
};

const STAGE_VARIANT: Record<string, BadgeVariant> = {
  swept: 'green',
  failed: 'destructive',
  skipped: 'gray',
  pending: 'secondary',
};

/** Stages a retry makes sense for: the sweep did not move the funds, and nothing is running. */
const RETRYABLE_STAGES = new Set(['failed', 'skipped', 'pending']);

/** One sweep: where the merchant's funds went, the platform's share, and why it failed when it did. */
export function PayoutRow({ payout, canRetry, isRetrying, onRetry }: Props) {
  const { t, i18n } = useTranslation('manageCryptoPayments');
  const explorerUrl = safeHttpUrl(payout.explorerTxUrl);
  const platform = payout.platform;
  const isRetryable = canRetry && RETRYABLE_STAGES.has(payout.stage);

  return (
    <TableRow>
      <TableCell className="align-top">
        <div className="text-sm">{payout.storeName ?? t('payments.unknownStore')}</div>
        <div className="text-muted-foreground text-xs">{payout.chainName}</div>
      </TableCell>
      <TableCell className="whitespace-nowrap align-top">
        <div className="font-mono text-sm">
          {payout.amount ?? '—'} {payout.asset}
        </div>
        {payout.merchantAmount && (
          <div className="text-muted-foreground text-xs">
            {t('payouts.toMerchant', { amount: payout.merchantAmount, asset: payout.asset })}
          </div>
        )}
      </TableCell>
      <TableCell className="align-top">
        {platform?.waivedReason ? (
          <span className="text-muted-foreground text-xs">{t('payouts.waived', { reason: platform.waivedReason })}</span>
        ) : platform?.amount ? (
          <div>
            <div className="font-mono text-sm">
              {platform.amount} {payout.asset}
            </div>
            {platform.bps !== null && (
              <div className="text-muted-foreground text-xs">{formatBps(platform.bps, i18n.language)}</div>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground text-xs">—</span>
        )}
      </TableCell>
      <TableCell className="align-top">
        <Badge variant={STAGE_VARIANT[payout.stage] ?? 'blue'}>{t(`payouts.stages.${payout.stage}`, payout.stage)}</Badge>
        {payout.error && <div className="text-destructive mt-1 max-w-xs break-words text-xs">{payout.error}</div>}
        {payout.attempts > 1 && (
          <div className="text-muted-foreground text-xs">{t('payouts.attempts', { count: payout.attempts })}</div>
        )}
      </TableCell>
      <TableCell className="align-top">
        {payout.sweepTxHash ? (
          <div className="flex items-center gap-1">
            <CopyableValue
              value={payout.sweepTxHash}
              display={truncateMiddle(payout.sweepTxHash)}
              label={t('payouts.columns.sweepTx')}
              className="text-xs"
            />
            {explorerUrl && (
              <a
                href={explorerUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('payments.viewOnExplorer')}
                className="text-muted-foreground hover:text-foreground"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground text-xs">—</span>
        )}
      </TableCell>
      <TableCell className="text-muted-foreground whitespace-nowrap align-top text-xs">
        {formatDate(payout.createdAt)}
      </TableCell>
      <TableCell className="text-right align-top">
        {isRetryable && (
          <Button type="button" variant="ghost" size="sm" disabled={isRetrying} onClick={() => onRetry(payout.id)}>
            <RotateCw className={isRetrying ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} />
            {isRetrying ? t('payouts.retrying') : t('payouts.retry')}
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}
